import React, { useEffect, useMemo, useState } from "react";
import FiltroMultiple from "../../../Postventa/Dashboard/Modales/FiltroMultiple";

const ESTADO_VACIO_FILTROS = { asesor: [], rfc: [], razonSocial: [], tipoPersona: [], etiqueta: [], sucursal: [], validado: [] };
const ESTADO_VACIO_MOSTRAR = {
  asesor: { open: false },
  rfc: { open: false },
  razonSocial: { open: false },
  tipoPersona: { open: false },
  etiqueta: { open: false },
  sucursal: { open: false },
  validado: { open: false },
};

const textoValidado = (v) => (v === 1 || v === "1" ? "Sí" : "No");
const textoTipoPersona = (v) => (v === "F" ? "Física" : v === "M" ? "Moral" : v || "—");

// Tabla agrupada por asesor — colapsable, mismo patrón que ReporteVisitasTabla (Visitas
// Programadas): click en la fila de asesor despliega/contrae sus clientes; con un filtro de
// columna activo el grupo inicia desplegado pero el usuario puede contraerlo igual.
// "listado" llega plano desde ReportesCRM/ReporteInformacionCapturada — cada fila es un
// cliente con idcliente, rfc, razon_social, tipo_persona, etiqueta, etiqueta_texto, idvendedor,
// vendedor, idsucursal, sucursal, validado (no se muestran los ids).
export const ReporteClientesTabla = ({ listado: listadoProp }) => {
  const listado = Array.isArray(listadoProp) ? listadoProp : [];
  const [options, setOptions] = useState(ESTADO_VACIO_FILTROS);
  const [filtros, setFiltros] = useState(ESTADO_VACIO_FILTROS);
  const [filtrosTemp, setFiltrosTemp] = useState(ESTADO_VACIO_FILTROS);
  const [mostrarFiltro, setMostrarFiltro] = useState(ESTADO_VACIO_MOSTRAR);
  const [prioridadFiltros, setPrioridadFiltros] = useState([]);
  const [abiertos, setAbiertos] = useState({});

  const onToggle = (idvendedor) => {
    setAbiertos((prev) => ({ ...prev, [idvendedor]: !prev[idvendedor] }));
  };

  useEffect(() => {
    setOptions({
      asesor: [...new Set(listado.map((c) => c.vendedor).filter(Boolean))].map((v) => ({ value: v, label: v })),
      rfc: [...new Set(listado.map((c) => c.rfc).filter(Boolean))].map((v) => ({ value: v, label: v })),
      razonSocial: [...new Set(listado.map((c) => c.razon_social).filter(Boolean))].map((v) => ({ value: v, label: v })),
      tipoPersona: [...new Set(listado.map((c) => c.tipo_persona).filter(Boolean))].map((v) => ({ value: v, label: textoTipoPersona(v) })),
      etiqueta: [...new Set(listado.map((c) => c.etiqueta_texto).filter(Boolean))].map((v) => ({ value: v, label: v })),
      sucursal: [...new Set(listado.map((c) => c.sucursal).filter(Boolean))].map((v) => ({ value: v, label: v })),
      validado: [0, 1].map((v) => ({ value: v, label: textoValidado(v) })),
    });
  }, [listado]);

  const actualizarPrioridad = (filtro) => {
    setPrioridadFiltros((prev) => {
      const index = prev.indexOf(filtro);
      const nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

      setFiltrosTemp((prevTemp) => {
        const limpio = { ...prevTemp };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      setFiltros((prevFiltros) => {
        const limpio = { ...prevFiltros };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      return nuevasPrioridades;
    });
  };

  const asesoresAgrupados = useMemo(() => {
    let data = [...listado];

    prioridadFiltros.forEach((filtro) => {
      if (filtro === "asesor" && filtros.asesor.length > 0) {
        data = data.filter((c) => filtros.asesor.includes(c.vendedor));
      }
      if (filtro === "rfc" && filtros.rfc.length > 0) {
        data = data.filter((c) => filtros.rfc.includes(c.rfc));
      }
      if (filtro === "razonSocial" && filtros.razonSocial.length > 0) {
        data = data.filter((c) => filtros.razonSocial.includes(c.razon_social));
      }
      if (filtro === "tipoPersona" && filtros.tipoPersona.length > 0) {
        data = data.filter((c) => filtros.tipoPersona.includes(c.tipo_persona));
      }
      if (filtro === "etiqueta" && filtros.etiqueta.length > 0) {
        data = data.filter((c) => filtros.etiqueta.includes(c.etiqueta_texto));
      }
      if (filtro === "sucursal" && filtros.sucursal.length > 0) {
        data = data.filter((c) => filtros.sucursal.includes(c.sucursal));
      }
      if (filtro === "validado" && filtros.validado.length > 0) {
        data = data.filter((c) => filtros.validado.includes(c.validado));
      }
    });

    const grupos = new Map();
    data.forEach((c) => {
      if (!grupos.has(c.idvendedor)) {
        grupos.set(c.idvendedor, {
          idvendedor: c.idvendedor,
          vendedor: c.vendedor,
          sucursal: c.sucursal,
          clientes: [],
          validados: 0,
        });
      }
      const grupo = grupos.get(c.idvendedor);
      grupo.clientes.push(c);
      if (c.validado === 1 || c.validado === "1") grupo.validados += 1;
    });

    // idvendedor -1 y 0 (sin asesor asignado) siempre al final, después de los asesores reales
    const esSinAsesor = (idvendedor) => idvendedor === -1 || idvendedor === 0;
    return [...grupos.values()].sort((a, b) => {
      const aSinAsesor = esSinAsesor(a.idvendedor);
      const bSinAsesor = esSinAsesor(b.idvendedor);
      if (aSinAsesor !== bSinAsesor) return aSinAsesor ? 1 : -1;
      return (a.vendedor || "").localeCompare(b.vendedor || "", "es");
    });
  }, [listado, filtros, prioridadFiltros]);

  const totalClientes = asesoresAgrupados.reduce((acc, a) => acc + a.clientes.length, 0);
  const totalValidados = asesoresAgrupados.reduce((acc, a) => acc + a.validados, 0);

  const filtroProps = (field, label) => ({
    label,
    field,
    options: options[field],
    filtros,
    setFiltros,
    filtrosTemp,
    setFiltrosTemp,
    mostrarFiltro,
    setMostrarFiltro,
    actualizarPrioridad,
    setPrioridadFiltros,
  });

  return (
    <div className="crmcs-cot-tabla-wrap">
      <div className="crmcs-cot-tabla-scroll scroll">
        <table cellPadding={0} cellSpacing={0}>
          <thead>
            <tr>
              <th>
                <div>
                  <FiltroMultiple {...filtroProps("asesor", "Asesor")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("rfc", "RFC")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("razonSocial", "Razón social")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("tipoPersona", "Tipo de persona")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("etiqueta", "Etiqueta")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("sucursal", "Sucursal")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("validado", "Validado")} />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {asesoresAgrupados.map((asesor) => {
              const expandido = !!abiertos[asesor.idvendedor];
              return (
                <React.Fragment key={asesor.idvendedor}>
                  <tr className="rca-row-asesor" onClick={() => onToggle(asesor.idvendedor)}>
                    <td className="rca-td-asesor">
                      <span className="rca-flecha">{expandido ? "▼" : "▶"}</span>
                      <strong>{asesor.vendedor}</strong>
                      {asesor.idvendedor !== -1 && asesor.idvendedor !== 0 && (
                        <span className="rca-td-asesor-sucursal">{asesor.sucursal}</span>
                      )}
                    </td>
                    <td colSpan={4}></td>
                    <td className="rca-td-contador" colSpan={2}>
                      {asesor.validados}/{asesor.clientes.length} validados
                    </td>
                  </tr>

                  {expandido && asesor.clientes.map((c) => (
                    <tr key={c.idcliente} className="rca-row-cliente">
                      <td></td>
                      <td className="rca-td-cliente">{c.rfc || "—"}</td>
                      <td className="rca-td-cliente">{c.razon_social}</td>
                      <td>{textoTipoPersona(c.tipo_persona)}</td>
                      <td>{c.etiqueta_texto || "—"}</td>
                      <td>{c.sucursal || "—"}</td>
                      <td>
                        <span className={`rca-verificado-pill ${c.validado === 1 || c.validado === "1" ? "si" : "no"}`}>
                          {textoValidado(c.validado)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="crmcs-cot-tabla-total">
        <span>Total ({asesoresAgrupados.length} asesores)</span>
        <span>{totalValidados}/{totalClientes} validados</span>
      </div>
    </div>
  );
};
